/**
 * True when two URLs point at the same object — same origin + pathname — differing
 * only in the query string (e.g. a refreshed SigV4 signature for unchanged bytes).
 * Falls back to a query-stripped string compare when either value is not a valid URL.
 */
export function sameObjectUrl(a: string, b: string): boolean {
  if (a === b) return true;
  try {
    const ua = new URL(a);
    const ub = new URL(b);
    return ua.origin === ub.origin && ua.pathname === ub.pathname;
  } catch {
    return a.split('?')[0] === b.split('?')[0];
  }
}

/**
 * Returns a Map containing only the entries of `map` whose key is in `present`.
 * Returns the SAME map reference when nothing would be pruned, so callers can pass
 * the result to setState without forcing a needless re-render.
 */
export function retainKeys<V>(map: Map<string, V>, present: Set<string>): Map<string, V> {
  let pruned = false;
  const next = new Map<string, V>();
  for (const [k, v] of map) {
    if (present.has(k)) next.set(k, v);
    else pruned = true;
  }
  return pruned ? next : map;
}

/**
 * Set counterpart of {@link retainKeys}: returns a Set containing only the
 * members of `set` that are in `present`. Returns the SAME set reference when
 * nothing would be pruned, so callers can pass the result to setState without
 * forcing a needless re-render.
 */
export function retainSetKeys(set: Set<string>, present: Set<string>): Set<string> {
  let pruned = false;
  const next = new Set<string>();
  for (const k of set) {
    if (present.has(k)) next.add(k);
    else pruned = true;
  }
  return pruned ? next : set;
}
